import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'

interface ImportProgress {
  done: number
  total: number
}

interface TranslationRowActionsProps {
  investigationId: string
  isExporting: boolean
  isImporting: boolean
  importProgress: ImportProgress | null
  onExport: (investigationId: string) => void
  onImport: (investigationId: string) => void
}

/** Row actions for one investigation on the translation dashboard: the
 * existing "Modifier"/"Edit" link, plus export/import for the per-
 * investigation translation transfer feature. Extracted from `page.tsx` to
 * keep the table markup readable. */
export function TranslationRowActions({
  investigationId,
  isExporting,
  isImporting,
  importProgress,
  onExport,
  onImport,
}: TranslationRowActionsProps) {
  const { t } = useTranslation()
  const importLabel = importProgress
    ? t('translations.dashboard_import_in_progress_percent', {
        percent: Math.round((importProgress.done / importProgress.total) * 100),
      })
    : t('translations.dashboard_import_in_progress')

  return (
    <div className="flex items-center gap-3 text-xs">
      <Link
        to="/manage/investigations/$id/edit"
        params={{ id: investigationId }}
        className="text-primary hover:underline"
      >
        {t('translations.dashboard_edit_link')}
      </Link>
      <button
        type="button"
        className="text-primary hover:underline disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1"
        onClick={() => onExport(investigationId)}
        disabled={isExporting}
      >
        {isExporting && <Loader2 className="w-3 h-3 animate-spin" />}
        {t('translations.dashboard_export_action')}
      </button>
      <button
        type="button"
        className="text-primary hover:underline disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1"
        onClick={() => onImport(investigationId)}
        disabled={isImporting}
      >
        {isImporting && <Loader2 className="w-3 h-3 animate-spin" />}
        {isImporting ? importLabel : t('translations.dashboard_import_action')}
      </button>
    </div>
  )
}
